import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldAlert, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Non-dismissible modal shown when the account is flagged
// must_change_password (e.g. a freshly-seeded admin using its temporary
// password). Forces a rotation before the user can continue.
export default function ForcePasswordChangeModal() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async () => {
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: getAuthHeader() },
      );
      toast.success('Password updated — thanks for securing your account');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="force-password-modal"
    >
      <div className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-brand-surface p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="rounded-full bg-amber-500/15 p-2">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Set a new password</h2>
        </div>
        <p className="text-sm text-gray-400 mb-5">
          For your security, you must change the temporary password on this account before continuing.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-white">Temporary / current password</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 bg-white/5 border-white/10 text-white"
              data-testid="force-password-current"
            />
          </div>
          <div>
            <Label className="text-white">New password</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="At least 8 characters"
              className="mt-1 bg-white/5 border-white/10 text-white"
              data-testid="force-password-new"
            />
          </div>
          <div>
            <Label className="text-white">Confirm new password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 bg-white/5 border-white/10 text-white"
              data-testid="force-password-confirm"
            />
          </div>
          <Button
            onClick={submit}
            disabled={saving || !current || !next || !confirm}
            className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
            data-testid="force-password-submit"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Update password & continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
